import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  useElderCare 
} from '@/contexts/ElderCareContext';
import { useAppState } from '@/contexts/AppStateContext';
import EmergencyBrief from '@/components/elder/EmergencyBrief';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import {
  Heart, Activity, Pill, Calendar, Car, Users, FileText,
  AlertCircle, CheckCircle, X, Plus, Copy, Trash2,
  Upload, Phone, Mail, User, Clock, ShieldAlert, Stethoscope,
  ChevronRight, Download, Eye, Info, Printer, Settings
} from 'lucide-react';

// --- Constants & Helpers ---

const DNR_LABELS: Record<string, string> = {
  'full': 'FULL DNR',
  'dnr-cc': 'DNR — COMFORT CARE ONLY',
  'partial': 'PARTIAL DNR',
  'none': 'No DNR'
};

type Tab = 'overview' | 'health' | 'medications' | 'appointments' | 'transport' | 'care-team' | 'notes' | 'documents' | 'care-circle';

// Simple date formatter
const formatDate = (date: string | Date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const formatTime = (date: string | Date) => new Date(date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const formatDateTime = (date: string | Date) => `${formatDate(date)} at ${formatTime(date)}`;

// --- Components ---

// Generic Modal Wrapper
const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; size?: 'sm' | 'md' | 'lg' }> = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full ${size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export const ElderCareProfile: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();
  const { state: appState } = useAppState();
  
  const { 
    careProfiles, allVitals, allCheckIns, allMedications, allAdherence,
    allAppointments, allIncidents, allTransportRequests, allNotes,
    allDocuments, allCareTeam, callElderCare,
    getTodayAdherenceSummary, getUpcomingAppointments 
  } = useElderCare();

  const profile = careProfiles.find(p => p.id === profileId);
  const currentUserId = appState?.currentUser?.id;

  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [isEmergencyBriefOpen, setIsEmergencyBriefOpen] = useState(false);
  const [isDnrModalOpen, setIsDnrModalOpen] = useState(false);

  // Role Logic
  const userRole = useMemo(() => {
    return profile?.careCircle?.find(m => m.userId === currentUserId)?.role;
  }, [profile, currentUserId]);
  
  const isCareAdmin = userRole === 'care-admin';

  // --- State for Modals & Forms ---
  
  // Health Tab
  const [isLogVitalsOpen, setIsLogVitalsOpen] = useState(false);
  const [vitalsForm, setVitalsForm] = useState({ bloodPressureSystolic: '', bloodPressureDiastolic: '', heartRate: '', weight: '', bloodGlucose: '', spo2: '', temperature: '', notes: '' });
  
  const [isLogCheckInOpen, setIsLogCheckInOpen] = useState(false);
  const [checkInForm, setCheckInForm] = useState({ painLevel: '0', generalFeeling: 'okay', needsHelp: false, notes: '' });

  const [isLogIncidentOpen, setIsLogIncidentOpen] = useState(false);
  const [incidentForm, setIncidentForm] = useState({ date: new Date().toISOString().split('T')[0], type: 'fall', severity: 'mild', description: '', medicalAttentionSought: false });

  // Medications Tab
  const [isAddMedicationOpen, setIsAddMedicationOpen] = useState(false);
  const [medForm, setMedForm] = useState({ name: '', dosage: '', frequency: 'daily', times: '', startDate: '', quantity: 0, refillThreshold: 0 });

  const [isLogAdherenceOpen, setIsLogAdherenceOpen] = useState(false);
  const [adherenceForm, setAdherenceForm] = useState<Record<string, 'taken' | 'skipped'>>({});

  // Appointments Tab
  const [isCreateAppointmentOpen, setIsCreateAppointmentOpen] = useState(false);
  const [apptForm, setApptForm] = useState({ title: '', type: 'checkup', providerName: '', providerPhone: '', date: '', time: '', location: '', notes: '' });

  // Transport Tab
  const [isCreateTransportOpen, setIsCreateTransportOpen] = useState(false);
  const [transportForm, setTransportForm] = useState({ date: '', time: '', pickupLocation: '', dropoffLocation: '', reason: '', needsWheelchair: false, isHousehold: true, assigneeName: '', providerName: '', providerPhone: '' });

  // Care Team Tab
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', role: 'doctor', phone: '', email: '', specialty: '', notes: '' });

  // Notes Tab
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ type: 'general', content: '', isUrgent: false });

  // Documents Tab
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [docForm, setDocForm] = useState({ name: '', type: 'other', url: '' });

  // Care Circle Tab
  const [isInviteMemberOpen, setIsInviteMemberOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState('contributor');
  const [inviteCode, setInviteCode] = useState('');
  const [isLinkHouseholdOpen, setIsLinkHouseholdOpen] = useState(false);
  const [linkHouseholdId, setLinkHouseholdId] = useState('');

  const { t } = useTranslation();

  // --- Derived Data ---

  const latestVitals = useMemo(() => {
    if (!allVitals || !profileId) return null;
    // Sort by date desc and take first
    const vitals = allVitals.get(profileId!) ?? [];
    return vitals.sort((a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime())[0] ?? null;
  }, [allVitals, profileId]);

  const recentIncidents = useMemo(() => {
    if (!allIncidents || !profileId) return [];
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return (allIncidents.get(profileId!) ?? [])
      .filter(i => new Date(i.date) >= thirtyDaysAgo)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allIncidents, profileId]);

  const todayAdherence = useMemo(() => {
    return getTodayAdherenceSummary ? getTodayAdherenceSummary(profileId!) : { taken: 0, due: 0 };
  }, [getTodayAdherenceSummary, profileId]);

  const elderMedications = useMemo(() => {
    if (!allMedications) return [];
    return allMedications.get(profileId!) ?? [];
  }, [allMedications, profileId]);

  const upcomingAppointments = useMemo(() => {
    if (!allAppointments) return [];
    return (allAppointments.get(profileId!) ?? [])
      .filter(a => a.status === 'upcoming')
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  }, [allAppointments, profileId]);

  const transportRequests = useMemo(() => {
    if (!allTransportRequests) return [];
    return (allTransportRequests.get(profileId!) ?? [])
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [allTransportRequests, profileId]);

  const elderNotes = useMemo(() => {
    if (!allNotes) return [];
    return (allNotes.get(profileId!) ?? [])
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allNotes, profileId]);

  const elderDocuments = useMemo(() => {
    if (!allDocuments) return [];
    const docs = allDocuments.get(profileId!) ?? [];
    // Sort DNR to top
    const dnr = docs.find(d => d.category === 'legal');
    const others = docs.filter(d => d !== dnr);
    return dnr ? [dnr, ...others] : others;
  }, [allDocuments, profileId]);


  // --- Handlers ---

  const handleLogVitals = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('logVitals', { profileId: profileId!, ...vitalsForm });
    setIsLogVitalsOpen(false);
    setVitalsForm({ bloodPressureSystolic: '', bloodPressureDiastolic: '', heartRate: '', weight: '', bloodGlucose: '', spo2: '', temperature: '', notes: '' });
  };

  const handleLogCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('logCheckIn', { profileId: profileId!, ...checkInForm });
    setIsLogCheckInOpen(false);
  };

  const handleLogIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('logIncident', { profileId: profileId!, ...incidentForm });
    setIsLogIncidentOpen(false);
  };

  const handleAddMedication = async (e: React.FormEvent) => {
    e.preventDefault();
    const scheduledTimes = medForm.times.split(',').map(t => t.trim()).filter(t => t);
    await callElderCare('addMedication', {
      profileId: profileId!,
      name: medForm.name,
      dosage: medForm.dosage,
      frequency: medForm.frequency,
      scheduledTimes,
      startDate: medForm.startDate,
      currentQuantity: medForm.quantity,
      refillThreshold: medForm.refillThreshold,
    });
    setIsAddMedicationOpen(false);
  };

  const handleLogAdherence = async (e: React.FormEvent) => {
    e.preventDefault();
    // Loop through form and call logAdherence per med
    for (const [medId, status] of Object.entries(adherenceForm)) {
      await callElderCare('logAdherence', { profileId: profileId!, medicationId: medId, status });
    }
    setIsLogAdherenceOpen(false);
  };

  const handleCreateAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('createAppointment', {
      profileId: profileId!,
      date: apptForm.date,
      time: apptForm.time,
      providerName: apptForm.providerName,
      providerLocation: apptForm.location,
      reason: apptForm.title || apptForm.type,
      notes: apptForm.notes,
    });
    setIsCreateAppointmentOpen(false);
  };

  const handleCreateTransport = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('createTransportRequest', {
      profileId: profileId!,
      date: transportForm.date,
      time: transportForm.time,
      pickupLocation: transportForm.pickupLocation,
      dropoffLocation: transportForm.dropoffLocation,
      reason: transportForm.reason,
      needsWheelchair: transportForm.needsWheelchair,
    });
    setIsCreateTransportOpen(false);
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('addCareTeamContact', { profileId: profileId!, ...contactForm });
    setIsAddContactOpen(false);
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('addNote', { profileId: profileId!, ...noteForm });
    setIsAddNoteOpen(false);
  };

  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('addDocument', { profileId: profileId!, ...docForm });
    setIsAddDocumentOpen(false);
  };

  const handleInvite = async () => {
    const res: any = await callElderCare('inviteCareCircleMember', { profileId, role: inviteRole });
    if (res?.inviteCode) setInviteCode(res.inviteCode);
  };

  const handleRemoveMember = async (userId: string) => {
    if (confirm(t('elder.confirm_remove_member', 'Are you sure you want to remove this member?'))) {
      await callElderCare('removeCareCircleMember', { profileId, targetUserId: userId });
    }
  };

  const handleLinkHousehold = async (e: React.FormEvent) => {
    e.preventDefault();
    await callElderCare('linkHousehold', { profileId, householdId: linkHouseholdId });
    setIsLinkHouseholdOpen(false);
  };

  if (!profile) return <div className="p-8 text-center">{t('elder.profile_not_found', 'Profile not found')}</div>;

  // --- Render Functions ---

  const ELDER_TABS: TabItem[] = [
    { id: 'overview',    label: t('elder.tab_overview', 'Overview') },
    { id: 'health',      label: t('elder.tab_health', 'Health') },
    { id: 'medications', label: t('elder.tab_medications', 'Medications') },
    { id: 'appointments',label: t('elder.tab_appointments', 'Appointments') },
    { id: 'transport',   label: t('elder.tab_transport', 'Transport') },
    { id: 'care-team',   label: t('elder.tab_care_team', 'Care Team') },
    { id: 'notes',       label: t('elder.tab_notes', 'Notes') },
    { id: 'documents',   label: t('elder.tab_documents', 'Documents') },
    { id: 'care-circle', label: t('elder.tab_care_circle', 'Care Circle') },
  ];

  const renderTabList = () => (
    <div className="bg-white dark:bg-slate-900 px-4 sticky top-0 z-20">
      <TabBar
        tabs={ELDER_TABS}
        activeTab={activeTab}
        onChange={id => setActiveTab(id as Tab)}
        variant="underline"
      />
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6 animate-fade-in">
      {/* DNR Banner */}
      {profile.dnrStatus !== 'none' && (
        <div 
          onClick={() => setIsDnrModalOpen(true)}
          className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-xl flex items-center justify-between cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <ShieldAlert className="text-red-600 dark:text-red-400 h-6 w-6" />
            <div>
              <p className="font-bold text-red-700 dark:text-red-300">{t('elder.dnr_status', 'DNR Status')}: {DNR_LABELS[profile.dnrStatus]}</p>
              <p className="text-xs text-red-600 dark:text-red-400">{t('elder.click_for_details', 'Click for details')}</p>
            </div>
          </div>
          <ChevronRight className="text-red-400" />
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{t('elder.last_checkin', 'Last Check-In')}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {(() => { const sorted = (allCheckIns.get(profileId!) ?? []).sort((a,b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()); return sorted[0] ? formatDate(sorted[0].date) : 'N/A'; })()}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
(allCheckIns.get(profileId!) ?? [])[0]?.generalFeeling === 'better' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
{(allCheckIns.get(profileId!) ?? [])[0]?.generalFeeling || 'Unknown'}
            </span>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{t('elder.todays_meds', "Today's Meds")}</p>
          <div className="flex items-center gap-2 mt-1">
            <Pill className="text-blue-500 h-5 w-5" />
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {todayAdherence.taken} / {todayAdherence.due} {t('elder.taken', 'Taken')}
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{t('elder.next_appointment', 'Next Appointment')}</p>
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="text-purple-500 h-5 w-5" />
            <span className="text-lg font-medium text-gray-900 dark:text-white truncate">
              {upcomingAppointments[0] ? `${upcomingAppointments[0].providerName} (${formatDate(upcomingAppointments[0].dateTime)})` : t('elder.none_scheduled', 'None scheduled')}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conditions & Allergies */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('elder.health_profile', 'Health Profile')}</h3>
          <div className="mb-4">
            <p className="text-sm text-gray-500 mb-2">{t('elder.primary_conditions', 'Primary Conditions')}</p>
            <div className="flex flex-wrap gap-2">
              {profile.primaryConditions?.map((c: string, i: number) => (
                <span key={i} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300">{c}</span>
              )) || <span className="text-gray-400 italic">{t('elder.none_listed', 'None listed')}</span>}
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">{t('elder.allergies', 'Allergies')}</p>
            <div className="flex flex-wrap gap-2">
              {profile.allergies?.map((a: string, i: number) => (
                <span key={i} className="px-3 py-1 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full text-sm">{a}</span>
              )) || <span className="text-gray-400 italic">{t('elder.none_listed', 'None listed')}</span>}
            </div>
          </div>
        </div>

        {/* Recent Incidents */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('elder.recent_incidents', 'Recent Incidents (30d)')}</h3>
            <button onClick={() => setIsLogIncidentOpen(true)} className="text-xs text-brand hover:underline">{t('elder.log_new', 'Log New')}</button>
          </div>
          <div className="space-y-3">
            {recentIncidents.length > 0 ? recentIncidents.slice(0, 3).map(inc => (
              <div key={inc.id} className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-xl">
                <AlertCircle className="text-red-500 mt-0.5" size={16} />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{inc.type} - {inc.severity}</p>
                  <p className="text-xs text-gray-500">{formatDate(inc.date)}</p>
                </div>
              </div>
            )) : <p className="text-sm text-gray-500 italic">{t('elder.no_recent_incidents', 'No recent incidents.')}</p>}
          </div>
        </div>
      </div>

      {/* Care Circle & Actions */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">{t('elder.care_circle', 'Care Circle')}</h3>
        </div>
        <div className="flex -space-x-2 overflow-hidden mb-4">
          {profile.careCircle?.map((member: any) => (
            <div key={member.userId} className="inline-block h-10 w-10 rounded-full ring-2 ring-white dark:ring-gray-800 bg-gray-300 flex items-center justify-center text-xs font-bold" title={member.name}>
              {member.name?.[0] || 'U'}
            </div>
          ))}
          <button className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 hover:bg-gray-200">
            <Plus size={16} />
          </button>
        </div>
        <button 
          onClick={() => setIsEmergencyBriefOpen(true)}
          className="w-full py-3 px-4 bg-brand hover:bg-brand-hover text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-lg shadow-blue-600/20"
        >
          <Printer size={18} />
          {t('elder.emergency_brief', 'Emergency Brief')}
        </button>
      </div>
    </div>
  );

  const renderHealth = () => (
    <div className="space-y-6">
      {/* Vitals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: t('elder.blood_pressure', 'Blood Pressure'), val: latestVitals ? `${latestVitals.bloodPressureSystolic ?? '--'}/${latestVitals.bloodPressureDiastolic ?? '--'}` : '--', unit: 'mmHg', icon: Heart, color: 'text-red-500' },
          { label: t('elder.heart_rate', 'Heart Rate'), val: latestVitals?.heartRate ?? '--', unit: 'bpm', icon: Activity, color: 'text-pink-500' },
          { label: t('elder.weight', 'Weight'), val: latestVitals?.weight || '--', unit: 'lbs', icon: User, color: 'text-blue-500' },
          { label: t('elder.glucose', 'Glucose'), val: latestVitals?.bloodGlucose ?? '--', unit: 'mg/dL', icon: DropletIcon, color: 'text-orange-500' },
          { label: t('elder.spo2', 'SpO2'), val: latestVitals?.spo2 || '--', unit: '%', icon: WindIcon, color: 'text-cyan-500' },
          { label: t('elder.temp', 'Temp'), val: latestVitals?.temperature ?? '--', unit: '°F', icon: ThermometerIcon, color: 'text-yellow-500' },
        ].map((v, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center text-center">
            <v.icon className={`mb-2 ${v.color}`} size={24} />
            <p className="text-xs text-gray-500">{v.label}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">{v.val}</p>
            <p className="text-xs text-gray-400">{v.unit}</p>
          </div>
        ))}
        <button 
          onClick={() => setIsLogVitalsOpen(true)}
          className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl flex flex-col items-center justify-center text-gray-400 hover:border-brand hover:text-blue-500 transition-colors"
        >
          <Plus size={24} />
          <span className="text-xs mt-1">{t('elder.log_vitals', 'Log Vitals')}</span>
        </button>
      </div>

      {/* Check-ins */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
          <h3 className="font-semibold text-gray-900 dark:text-white">{t('elder.recent_checkins', 'Recent Check-ins')}</h3>
          <button onClick={() => setIsLogCheckInOpen(true)} className="text-sm text-brand font-medium">{t('elder.log_checkin', 'Log Check-In')}</button>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {(allCheckIns.get(profileId!) ?? []).sort((a,b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()).slice(0, 10).map(checkIn => (
            <div key={checkIn.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">{formatDate(checkIn.date)}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${checkIn.generalFeeling === 'better' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{checkIn.generalFeeling}</span>
                </div>
                <p className="text-sm text-gray-500">{t('elder.pain_label', 'Pain')}: {checkIn.painLevel}/3 • {checkIn.needsHelp ? t('elder.needs_help', 'Needs Help') : t('elder.self_sufficient', 'Self sufficient')}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Dummy icons for vitals to avoid errors if specific lucide icons aren't imported
  const DropletIcon = () => <div className="w-6 h-6 bg-orange-500 rounded-full" />;
  const WindIcon = () => <div className="w-6 h-6 bg-cyan-500 rounded-full" />;
  const ThermometerIcon = () => <div className="w-6 h-6 bg-yellow-500 rounded-full" />;

  const renderMedications = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">
          {t('elder.today_label', 'Today')}: <span className="font-bold text-gray-900 dark:text-white">{todayAdherence.taken}/{todayAdherence.due}</span> {t('elder.taken_lower', 'taken')}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setIsLogAdherenceOpen(true)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium text-sm hover:bg-blue-200">{t('elder.log_adherence', 'Log Adherence')}</button>
          <button onClick={() => setIsAddMedicationOpen(true)} className="px-4 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover flex items-center gap-1"><Plus size={16}/> {t('elder.add_med', 'Add Med')}</button>
        </div>
      </div>

      <div className="space-y-3">
        {elderMedications.map(med => (
          <div key={med.id} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-gray-900 dark:text-white">{med.name}</h4>
                {(!med.endDate || new Date(med.endDate) >= new Date()) ? <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{t('elder.active', 'Active')}</span> : <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">{t('elder.inactive', 'Inactive')}</span>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">{med.dosage} • {med.frequency}</p>
              <p className="text-xs text-gray-400 mt-1">Times: {med.scheduledTimes?.join(', ')}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{med.currentQuantity} left</p>
              {med.currentQuantity <= med.refillThreshold && (
                <p className="text-xs text-red-500 font-bold flex items-center justify-end gap-1"><AlertCircle size={12}/> {t('elder.refill_soon', 'Refill Soon')}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAppointments = () => (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={() => setIsCreateAppointmentOpen(true)} className="px-4 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover flex items-center gap-1"><Plus size={16}/> {t('elder.create_appointment', 'Create Appointment')}</button>
      </div>
      
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('elder.upcoming', 'Upcoming')}</h3>
        <div className="space-y-3">
          {upcomingAppointments.map(appt => (
            <div key={appt.id} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-gray-900 dark:text-white">{appt.providerName}</h4>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-lg">{appt.appointmentType}</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Calendar size={14}/> {formatDateTime(appt.dateTime)}</span>
                <span className="flex items-center gap-1"><MapPin size={14}/> {appt.location || 'TBD'}</span>
              </div>
              {appt.prepNotes && <div className="mt-2 p-2 bg-yellow-50 text-yellow-700 text-xs rounded">Note: {appt.prepNotes}</div>}
            </div>
          ))}
          {upcomingAppointments.length === 0 && <p className="text-gray-500 italic">{t('elder.no_upcoming_appointments', 'No upcoming appointments')}</p>}
        </div>
      </div>
    </div>
  );

  const MapPin = ({size}: {size: number}) => <div className="w-4 h-4 bg-gray-400 rounded-full" />;

  const renderTransport = () => (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={() => setIsCreateTransportOpen(true)} className="px-4 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover flex items-center gap-1"><Plus size={16}/> {t('elder.request_transport', 'Request Transport')}</button>
      </div>
      <div className="space-y-3">
        {transportRequests.map(t => (
          <div key={t.id} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between">
              <h4 className="font-bold text-gray-900 dark:text-white">{t.destination}</h4>
              <span className={`px-2 py-1 text-xs rounded-lg ${t.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{t.status}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{formatDate(t.date)} at {t.time} • {t.transportType}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCareTeam = () => (
    <div className="space-y-6">
      {profile.livingSituation === 'assisted-living' && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-2xl border border-indigo-100 dark:border-indigo-900">
          <h4 className="font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-2"><Stethoscope size={18}/> {t('elder.facility_info', 'Facility Info')}</h4>
          <p className="text-sm text-brand dark:text-indigo-400 mt-1">{profile.facilityName || 'Assisted Living Facility'}</p>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-900 dark:text-white">{t('elder.contacts', 'Contacts')}</h3>
        <button onClick={() => setIsAddContactOpen(true)} className="text-brand text-sm font-medium">+ {t('elder.add_contact', 'Add Contact')}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(allCareTeam.get(profileId!) ?? []).map(contact => (
          <div key={contact.id} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <User className="text-gray-500" size={20}/>
              </div>
              <div>
                <p className="font-bold text-gray-900 dark:text-white">{contact.name}</p>
                <p className="text-xs text-gray-500 capitalize">{contact.role}</p>
              </div>
            </div>
            <div className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-300">
              {contact.phone && <p className="flex items-center gap-2"><Phone size={14}/> {contact.phone}</p>}
              {contact.email && <p className="flex items-center gap-2"><Mail size={14}/> {contact.email}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderNotes = () => (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={() => setIsAddNoteOpen(true)} className="px-4 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover flex items-center gap-1"><Plus size={16}/> {t('elder.add_note', 'Add Note')}</button>
      </div>
      <div className="space-y-4">
        {elderNotes.map(note => (
          <div key={note.id} className={`p-4 rounded-2xl border ${note.isUrgent ? 'bg-red-50 dark:bg-red-900/20 border-red-100' : 'bg-white dark:bg-gray-800 border-gray-100'}`}>
            <div className="flex justify-between items-start mb-2">
              <span className="font-bold text-gray-900 dark:text-white text-sm">{note.authorName}</span>
              <span className="text-xs text-gray-500">{formatDateTime(note.createdAt)}</span>
            </div>
            <p className="text-gray-700 dark:text-gray-300 text-sm">{note.message}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-2 py-0.5 text-xs rounded-full ${note.type === 'concern' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{note.type}</span>
              {note.isUrgent && <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1"><AlertCircle size={10}/> {t('elder.urgent', 'Urgent')}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={() => setIsAddDocumentOpen(true)} className="px-4 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover flex items-center gap-1"><Upload size={16}/> {t('elder.add_document', 'Add Document')}</button>
      </div>
      <div className="space-y-3">
        {elderDocuments.map(doc => (
          <div key={doc.id} className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <FileText className="text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{doc.label}</p>
                <p className="text-xs text-gray-500">{doc.category} • {formatDate(doc.uploadedAt)}</p>
              </div>
            </div>
            <button className="text-brand hover:text-blue-800"><Download size={18}/></button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCareCircle = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-900 dark:text-white">{t('elder.members', 'Members')}</h3>
        <div className="flex gap-2">
           <button onClick={() => setIsLinkHouseholdOpen(true)} className="text-sm text-gray-600 hover:text-gray-900 underline">{t('elder.link_household', 'Link Household')}</button>
           {isCareAdmin && <button onClick={() => setIsInviteMemberOpen(true)} className="px-4 py-2 bg-brand text-white rounded-lg font-medium text-sm hover:bg-brand-hover">{t('elder.invite_member', 'Invite Member')}</button>}
        </div>
      </div>
      
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 dark:bg-gray-700 text-gray-500">
            <tr>
              <th className="px-4 py-3">{t('elder.col_name', 'Name')}</th>
              <th className="px-4 py-3">{t('elder.col_role', 'Role')}</th>
              <th className="px-4 py-3">{t('elder.col_joined', 'Joined')}</th>
              {isCareAdmin && <th className="px-4 py-3 text-right">{t('elder.col_actions', 'Actions')}</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {profile.careCircle?.map(member => (
              <tr key={member.userId}>
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{member.name}</td>
                <td className="px-4 py-3"><span className="px-2 py-1 bg-gray-100 rounded text-xs">{member.role}</span></td>
                <td className="px-4 py-3 text-gray-500">{formatDate(member.joinedAt)}</td>
                {isCareAdmin && (
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleRemoveMember(member.userId)} className="text-red-500 hover:text-red-700"><Trash2 size={16}/></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );


  // --- Main Render ---

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 p-4 shadow-sm border-b border-gray-200 dark:border-gray-700 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
          <ChevronRight className="rotate-180" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{profile.firstName} {profile.lastName}</h1>
          <p className="text-sm text-gray-500">{t('elder.elder_care_profile', 'Elder Care Profile')}</p>
        </div>
        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-brand font-bold">
          {profile.firstName?.[0]}
        </div>
      </div>

      {renderTabList()}

      <div className="flex-1 p-4 overflow-y-auto max-w-5xl mx-auto w-full">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'health' && renderHealth()}
        {activeTab === 'medications' && renderMedications()}
        {activeTab === 'appointments' && renderAppointments()}
        {activeTab === 'transport' && renderTransport()}
        {activeTab === 'care-team' && renderCareTeam()}
        {activeTab === 'notes' && renderNotes()}
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'care-circle' && renderCareCircle()}
      </div>

      {/* --- Modals --- */}

      {/* Emergency Brief */}
      <Modal isOpen={isEmergencyBriefOpen} onClose={() => setIsEmergencyBriefOpen(false)} title={t('elder.emergency_brief', 'Emergency Brief')} size="lg">
        <EmergencyBrief profile={profile} medications={elderMedications} onClose={() => setIsEmergencyBriefOpen(false)} />
      </Modal>

      {/* DNR Detail */}
      <Modal isOpen={isDnrModalOpen} onClose={() => setIsDnrModalOpen(false)} title={t('elder.dnr_status', 'DNR Status')}>
        <div className="text-center py-4">
          <ShieldAlert className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{DNR_LABELS[profile.dnrStatus]}</h2>
          <p className="text-gray-600 dark:text-gray-300">{t('elder.dnr_document_id', 'Document ID')}: {profile.dnrDocumentUrl || 'N/A'}</p>
          <p className="text-sm text-gray-500 mt-4">{t('elder.dnr_legally_binding', 'This status is legally binding and should be communicated to all care providers.')}</p>
        </div>
      </Modal>

      {/* Log Vitals */}
      <Modal isOpen={isLogVitalsOpen} onClose={() => setIsLogVitalsOpen(false)} title={t('elder.log_vitals', 'Log Vitals')}>
        <form onSubmit={handleLogVitals} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input type="number" placeholder={t('elder.placeholder_sys_bp', 'Sys (BP)')} className="input-field" value={vitalsForm.bloodPressureSystolic} onChange={e => setVitalsForm({...vitalsForm, bloodPressureSystolic: e.target.value})} required />
            <input type="number" placeholder={t('elder.placeholder_dia_bp', 'Dia (BP)')} className="input-field" value={vitalsForm.bloodPressureDiastolic} onChange={e => setVitalsForm({...vitalsForm, bloodPressureDiastolic: e.target.value})} required />
            <input type="number" placeholder={t('elder.placeholder_hr', 'HR (bpm)')} className="input-field" value={vitalsForm.heartRate} onChange={e => setVitalsForm({...vitalsForm, heartRate: e.target.value})} />
            <input type="number" placeholder={t('elder.placeholder_weight', 'Weight (lbs)')} className="input-field" value={vitalsForm.weight} onChange={e => setVitalsForm({...vitalsForm, weight: e.target.value})} />
            <input type="number" placeholder={t('elder.placeholder_glucose', 'Glucose')} className="input-field" value={vitalsForm.bloodGlucose} onChange={e => setVitalsForm({...vitalsForm, bloodGlucose: e.target.value})} />
            <input type="number" placeholder={t('elder.placeholder_spo2', 'SpO2 %')} className="input-field" value={vitalsForm.spo2} onChange={e => setVitalsForm({...vitalsForm, spo2: e.target.value})} />
            <input type="number" placeholder={t('elder.placeholder_temp', 'Temp (F)')} className="input-field" value={vitalsForm.temperature} onChange={e => setVitalsForm({...vitalsForm, temperature: e.target.value})} />
          </div>
          <textarea placeholder={t('common.notes', 'Notes')} className="input-field" value={vitalsForm.notes} onChange={e => setVitalsForm({...vitalsForm, notes: e.target.value})} />
          <button type="submit" className="w-full btn-primary">{t('elder.save_vitals', 'Save Vitals')}</button>
        </form>
      </Modal>

      {/* Log Check-In */}
      <Modal isOpen={isLogCheckInOpen} onClose={() => setIsLogCheckInOpen(false)} title={t('elder.log_checkin', 'Log Check-In')}>
        <form onSubmit={handleLogCheckIn} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('elder.pain_level', 'Pain Level')}</label>
            <div className="flex gap-2">
              {['0', '1', '2'].map(p => (
                <button key={p} type="button" onClick={() => setCheckInForm({...checkInForm, painLevel: p})} className={`flex-1 py-2 rounded-lg border ${checkInForm.painLevel === p ? 'bg-brand text-white border-brand' : 'border-gray-300'}`}>{p}</button>
              ))}
            </div>
          </div>
          <select className="input-field" value={checkInForm.generalFeeling} onChange={e => setCheckInForm({...checkInForm, generalFeeling: e.target.value})}>
            <option value="better">{t('elder.feeling_better', 'Better')}</option>
            <option value="same">{t('elder.feeling_same', 'Same')}</option>
            <option value="worse">{t('elder.feeling_worse', 'Worse')}</option>
          </select>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={checkInForm.needsHelp} onChange={e => setCheckInForm({...checkInForm, needsHelp: e.target.checked})} />
            {t('elder.needs_help_today', 'Needs Help Today')}
          </label>
          <textarea placeholder={t('common.notes', 'Notes')} className="input-field" value={checkInForm.notes} onChange={e => setCheckInForm({...checkInForm, notes: e.target.value})} />
          <button type="submit" className="w-full btn-primary">{t('elder.save_checkin', 'Save Check-In')}</button>
        </form>
      </Modal>

      {/* Log Incident */}
      <Modal isOpen={isLogIncidentOpen} onClose={() => setIsLogIncidentOpen(false)} title={t('elder.log_incident', 'Log Incident')}>
        <form onSubmit={handleLogIncident} className="space-y-4">
          <input type="date" className="input-field" value={incidentForm.date} onChange={e => setIncidentForm({...incidentForm, date: e.target.value})} />
          <select className="input-field" value={incidentForm.type} onChange={e => setIncidentForm({...incidentForm, type: e.target.value})}>
            <option value="fall">{t('elder.incident_fall', 'Fall')}</option>
            <option value="medical">{t('elder.incident_medical', 'Medical Emergency')}</option>
            <option value="behavioral">{t('elder.incident_behavioral', 'Behavioral')}</option>
            <option value="other">{t('elder.incident_other', 'Other')}</option>
          </select>
          <select className="input-field" value={incidentForm.severity} onChange={e => setIncidentForm({...incidentForm, severity: e.target.value})}>
            <option value="mild">{t('elder.severity_mild', 'Mild')}</option>
            <option value="moderate">{t('elder.severity_moderate', 'Moderate')}</option>
            <option value="serious">{t('elder.severity_serious', 'Serious')}</option>
          </select>
          <textarea placeholder={t('elder.placeholder_description', 'Description')} className="input-field" value={incidentForm.description} onChange={e => setIncidentForm({...incidentForm, description: e.target.value})} />
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={incidentForm.medicalAttentionSought} onChange={e => setIncidentForm({...incidentForm, medicalAttentionSought: e.target.checked})} />
            {t('elder.medical_attention_sought', 'Medical Attention Sought')}
          </label>
          <button type="submit" className="w-full btn-primary">{t('elder.save_incident', 'Save Incident')}</button>
        </form>
      </Modal>

      {/* Add Medication */}
      <Modal isOpen={isAddMedicationOpen} onClose={() => setIsAddMedicationOpen(false)} title={t('elder.add_medication', 'Add Medication')}>
        <form onSubmit={handleAddMedication} className="space-y-4">
          <input type="text" placeholder={t('elder.placeholder_med_name', 'Medication Name')} className="input-field" value={medForm.name} onChange={e => setMedForm({...medForm, name: e.target.value})} required />
          <input type="text" placeholder={t('elder.placeholder_dosage', 'Dosage (e.g. 10mg)')} className="input-field" value={medForm.dosage} onChange={e => setMedForm({...medForm, dosage: e.target.value})} required />
          <select className="input-field" value={medForm.frequency} onChange={e => setMedForm({...medForm, frequency: e.target.value})}>
            <option value="daily">{t('elder.freq_daily', 'Daily')}</option>
            <option value="weekly">{t('elder.freq_weekly', 'Weekly')}</option>
            <option value="asneeded">{t('elder.freq_as_needed', 'As Needed')}</option>
          </select>
          <input type="text" placeholder={t('elder.placeholder_times', 'Times (e.g. 8:00 AM, 8:00 PM)')} className="input-field" value={medForm.times} onChange={e => setMedForm({...medForm, times: e.target.value})} />
          <div className="grid grid-cols-2 gap-2">
             <input type="number" placeholder={t('elder.placeholder_current_qty', 'Current Qty')} className="input-field" value={medForm.quantity} onChange={e => setMedForm({...medForm, quantity: Number(e.target.value)})} />
             <input type="number" placeholder={t('elder.placeholder_refill_threshold', 'Refill Threshold')} className="input-field" value={medForm.refillThreshold} onChange={e => setMedForm({...medForm, refillThreshold: Number(e.target.value)})} />
          </div>
          <button type="submit" className="w-full btn-primary">{t('elder.add_medication', 'Add Medication')}</button>
        </form>
      </Modal>

      {/* Log Adherence */}
      <Modal isOpen={isLogAdherenceOpen} onClose={() => setIsLogAdherenceOpen(false)} title={t('elder.log_todays_adherence', "Log Today's Adherence")}>
        <form onSubmit={handleLogAdherence} className="space-y-4">
          {elderMedications.filter(m => m.isActive).map(med => (
            <div key={med.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">{med.name}</p>
                <p className="text-xs text-gray-500">{med.dosage} @ {med.scheduledTimes?.[0]}</p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setAdherenceForm({...adherenceForm, [med.id]: 'taken'})} className={`px-3 py-1 rounded text-sm ${adherenceForm[med.id] === 'taken' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>{t('elder.adherence_taken', 'Taken')}</button>
                <button type="button" onClick={() => setAdherenceForm({...adherenceForm, [med.id]: 'skipped'})} className={`px-3 py-1 rounded text-sm ${adherenceForm[med.id] === 'skipped' ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>{t('elder.adherence_skipped', 'Skipped')}</button>
              </div>
            </div>
          ))}
          <button type="submit" className="w-full btn-primary">{t('elder.save_adherence', 'Save Adherence')}</button>
        </form>
      </Modal>

      {/* Create Appointment */}
      <Modal isOpen={isCreateAppointmentOpen} onClose={() => setIsCreateAppointmentOpen(false)} title={t('elder.schedule_appointment', 'Schedule Appointment')}>
        <form onSubmit={handleCreateAppointment} className="space-y-4">
          <input type="text" placeholder={t('elder.placeholder_provider_name', 'Title / Provider Name')} className="input-field" value={apptForm.providerName} onChange={e => setApptForm({...apptForm, providerName: e.target.value})} required />
          <select className="input-field" value={apptForm.type} onChange={e => setApptForm({...apptForm, type: e.target.value})}>
            <option value="checkup">{t('elder.appt_checkup', 'Check-up')}</option>
            <option value="specialist">{t('elder.appt_specialist', 'Specialist')}</option>
            <option value="therapy">{t('elder.appt_therapy', 'Therapy')}</option>
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" className="input-field" value={apptForm.date} onChange={e => setApptForm({...apptForm, date: e.target.value})} required />
            <input type="time" className="input-field" value={apptForm.time} onChange={e => setApptForm({...apptForm, time: e.target.value})} required />
          </div>
          <input type="text" placeholder={t('elder.placeholder_location', 'Location')} className="input-field" value={apptForm.location} onChange={e => setApptForm({...apptForm, location: e.target.value})} />
          <textarea placeholder={t('elder.placeholder_prep_notes', 'Prep Notes')} className="input-field" value={apptForm.notes} onChange={e => setApptForm({...apptForm, notes: e.target.value})} />
          <button type="submit" className="w-full btn-primary">{t('elder.create_appointment', 'Create Appointment')}</button>
        </form>
      </Modal>

      {/* Create Transport */}
      <Modal isOpen={isCreateTransportOpen} onClose={() => setIsCreateTransportOpen(false)} title={t('elder.request_transport', 'Request Transport')}>
        <form onSubmit={handleCreateTransport} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <input type="date" className="input-field" value={transportForm.date} onChange={e => setTransportForm({...transportForm, date: e.target.value})} required />
            <input type="time" className="input-field" value={transportForm.time} onChange={e => setTransportForm({...transportForm, time: e.target.value})} required />
          </div>
          <input type="text" placeholder={t('elder.placeholder_pickup', 'Pickup Location')} className="input-field" value={transportForm.pickupLocation} onChange={e => setTransportForm({...transportForm, pickupLocation: e.target.value})} required />
          <input type="text" placeholder={t('elder.placeholder_dropoff', 'Drop-off Location')} className="input-field" value={transportForm.dropoffLocation} onChange={e => setTransportForm({...transportForm, dropoffLocation: e.target.value})} required />
          <input type="text" placeholder={t('elder.placeholder_reason', 'Reason / Purpose')} className="input-field" value={transportForm.reason} onChange={e => setTransportForm({...transportForm, reason: e.target.value})} required />
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={transportForm.needsWheelchair} onChange={e => setTransportForm({...transportForm, needsWheelchair: e.target.checked})} />
            {t('elder.needs_wheelchair', 'Needs Wheelchair Access')}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={transportForm.isHousehold} onChange={e => setTransportForm({...transportForm, isHousehold: e.target.checked})} />
            {t('elder.household_member', 'Household Member')}
          </label>
          {!transportForm.isHousehold && (
             <div className="space-y-2">
               <input type="text" placeholder={t('elder.placeholder_provider_name', 'Provider Name')} className="input-field" />
               <input type="tel" placeholder={t('elder.placeholder_provider_phone', 'Provider Phone')} className="input-field" />
             </div>
          )}
          <button type="submit" className="w-full btn-primary">{t('elder.request_transport', 'Request Transport')}</button>
        </form>
      </Modal>

      {/* Add Contact */}
      <Modal isOpen={isAddContactOpen} onClose={() => setIsAddContactOpen(false)} title={t('elder.add_care_team_contact', 'Add Care Team Contact')}>
        <form onSubmit={handleAddContact} className="space-y-4">
          <input type="text" placeholder={t('common.name', 'Name')} className="input-field" value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} required />
          <select className="input-field" value={contactForm.role} onChange={e => setContactForm({...contactForm, role: e.target.value})}>
            <option value="doctor">{t('elder.role_doctor', 'Doctor')}</option>
            <option value="nurse">{t('elder.role_nurse', 'Nurse')}</option>
            <option value="caregiver">{t('elder.role_caregiver', 'Caregiver')}</option>
            <option value="agency">{t('elder.role_agency', 'Agency')}</option>
          </select>
          <input type="tel" placeholder={t('common.phone', 'Phone')} className="input-field" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} required />
          <input type="email" placeholder={t('common.email', 'Email')} className="input-field" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} />
          <input type="text" placeholder={t('elder.placeholder_specialty', 'Specialty (optional)')} className="input-field" value={contactForm.specialty} onChange={e => setContactForm({...contactForm, specialty: e.target.value})} />
          <textarea placeholder={t('elder.placeholder_notes_optional', 'Notes (optional)')} className="input-field" value={contactForm.notes} onChange={e => setContactForm({...contactForm, notes: e.target.value})} />
          <button type="submit" className="w-full btn-primary">{t('elder.add_contact', 'Add Contact')}</button>
        </form>
      </Modal>

      {/* Add Note */}
      <Modal isOpen={isAddNoteOpen} onClose={() => setIsAddNoteOpen(false)} title={t('elder.add_note', 'Add Note')}>
        <form onSubmit={async (e) => { e.preventDefault(); await callElderCare('addNote', {profileId, ...noteForm}); setIsAddNoteOpen(false); setNoteForm({ type: 'general', content: '', isUrgent: false }); }} className="space-y-4">
          <select className="input-field" value={noteForm.type} onChange={e => setNoteForm({...noteForm, type: e.target.value})}>
            <option value="general">{t('elder.note_general', 'General')}</option>
            <option value="observation">{t('elder.note_observation', 'Observation')}</option>
            <option value="concern">{t('elder.note_concern', 'Concern')}</option>
            <option value="question">{t('elder.note_question', 'Question')}</option>
            <option value="positive">{t('elder.note_positive', 'Positive')}</option>
          </select>
          <textarea placeholder={t('elder.placeholder_note', 'Note...')} className="input-field min-h-[120px]" value={noteForm.content} onChange={e => setNoteForm({...noteForm, content: e.target.value})} required />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={noteForm.isUrgent} onChange={e => setNoteForm({...noteForm, isUrgent: e.target.checked})} />
            {t('elder.mark_urgent', 'Mark as urgent')}
          </label>
          <button type="submit" className="w-full btn-primary">{t('elder.save_note', 'Save Note')}</button>
        </form>
      </Modal>

      {/* Add Document */}
      <Modal isOpen={isAddDocumentOpen} onClose={() => setIsAddDocumentOpen(false)} title={t('elder.add_document', 'Add Document')}>
        <form onSubmit={async (e) => { e.preventDefault(); await callElderCare('addDocument', {profileId, ...docForm}); setIsAddDocumentOpen(false); setDocForm({ name: '', type: 'other', url: '' }); }} className="space-y-4">
          <input type="text" placeholder={t('elder.placeholder_doc_name', 'Document Name')} className="input-field" value={docForm.name} onChange={e => setDocForm({...docForm, name: e.target.value})} required />
          <select className="input-field" value={docForm.type} onChange={e => setDocForm({...docForm, type: e.target.value})}>
            <option value="legal">{t('elder.doc_legal', 'Legal')}</option>
            <option value="medical">{t('elder.doc_medical', 'Medical')}</option>
            <option value="insurance">{t('elder.doc_insurance', 'Insurance')}</option>
            <option value="financial">{t('elder.doc_financial', 'Financial')}</option>
            <option value="other">{t('elder.doc_other', 'Other')}</option>
          </select>
          <input type="url" placeholder={t('elder.placeholder_file_url', 'File URL')} className="input-field" value={docForm.url} onChange={e => setDocForm({...docForm, url: e.target.value})} required />
          <button type="submit" className="w-full btn-primary">{t('elder.add_document', 'Add Document')}</button>
        </form>
      </Modal>

      {/* Invite Member */}
      <Modal isOpen={isInviteMemberOpen} onClose={() => setIsInviteMemberOpen(false)} title={t('elder.invite_care_circle', 'Invite to Care Circle')}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('elder.invite_hint', 'Share this invite code with the person you want to add to the care circle.')}</p>
          <select className="input-field" value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
            <option value="contributor">{t('elder.role_contributor', 'Contributor')}</option>
            <option value="viewer">{t('elder.role_viewer', 'Viewer')}</option>
            <option value="care-admin">{t('elder.role_care_admin', 'Care Admin')}</option>
          </select>
          {inviteCode ? (
            <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <code className="flex-1 font-mono text-sm">{inviteCode}</code>
              <button onClick={() => navigator.clipboard.writeText(inviteCode)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                <Copy size={16} />
              </button>
            </div>
          ) : (
            <button onClick={handleInvite} className="w-full btn-primary">
              {t('elder.generate_invite_code', 'Generate Invite Code')}
            </button>
          )}
        </div>
      </Modal>

      {/* Link Household */}
      <Modal isOpen={isLinkHouseholdOpen} onClose={() => setIsLinkHouseholdOpen(false)} title={t('elder.link_household', 'Link Household')}>
        <form onSubmit={async (e) => { e.preventDefault(); await callElderCare('linkHousehold', {profileId, householdId: linkHouseholdId }); setIsLinkHouseholdOpen(false); setLinkHouseholdId(''); }} className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('elder.link_household_hint', 'Link a household to enable transport assignments from their members.')}</p>
          <input type="text" placeholder={t('elder.placeholder_household_id', 'Household ID')} className="input-field" value={linkHouseholdId} onChange={e => setLinkHouseholdId(e.target.value)} required />
          <button type="submit" className="w-full btn-primary">{t('elder.link_household', 'Link Household')}</button>
        </form>
      </Modal>

    </div>
  );
};

export default ElderCareProfile;
